// Display · Breadcrumb
//
// Navigation trail used at the top of entity-view pages.
// Each item links to a route (clickable) or is the current page (last item,
// plain text). No data fetching, no state. Pure props-in / events-out.
//
// All visual values come from `@ds/tokens/palette`.

import type { ReactNode } from "react";
import { Inline } from "@ds/layout/Inline";
import { TextValue } from "@ds/display/TextValue";
import {
  FONT_FAMILY,
  FONT_SIZE,
  FONT_WEIGHT,
  MOTION,
  RADIUS,
  TEXT,
  TONE,
} from "@ds/tokens/palette";

// ─── Hover CSS (injected once, idempotent) ────────────────────────────────────
//
// Rather than tracking hover state in React, we inject a single scoped style
// rule so breadcrumb links apply color + background transitions purely via CSS.
// Same idempotent-injection pattern used by @ds/editable/Field and EntityChip.

const BREADCRUMB_CSS_ID = "__ds-breadcrumb-hover";

function ensureBreadcrumbCSS(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(BREADCRUMB_CSS_ID)) return;
  const el = document.createElement("style");
  el.id = BREADCRUMB_CSS_ID;
  el.textContent = [
    // Shared chrome: hit-target padding (off-scale 1px 3px — see Breadcrumb.tsx),
    // negative margin to anchor baseline, color, font-size/weight, hover transition.
    `.ds-breadcrumb-link {
      display: inline-flex;
      align-items: center;
      font-size: ${FONT_SIZE.body}px;
      font-weight: ${FONT_WEIGHT.regular};
      font-family: ${FONT_FAMILY.sans};
      text-decoration: none;
      cursor: pointer;
      border-radius: ${RADIUS.xs}px;
      padding: 1px 3px;
      margin: -1px -3px;
      border: 0;
      background: transparent;
      color: ${TEXT.muted};
      transition: color ${MOTION.fast} ease, background ${MOTION.fast} ease;
    }`,
    `.ds-breadcrumb-link:hover { color: ${TEXT.primary}; background: ${TONE.grey.soft}; }`,
  ].join("\n");
  document.head.appendChild(el);
}

// ─── Inputs (data props — the model) ─────────────────────────────────────────

export interface BreadcrumbItem {
  /** Display label. */
  label: ReactNode;
  /**
   * Click-to-navigate target. If absent, the item renders as plain text
   * (typically the current page — the last item).
   */
  href?: string;
  /** Click handler. Useful when navigation is via React Router rather than href. */
  onClick?: () => void;
}

export interface BreadcrumbInput {
  items: BreadcrumbItem[];
  /** Separator between items. Default "›". */
  separator?: ReactNode;
  testId?: string;
}

// ─── Outputs (events emitted — controller hooks) ──────────────────────────────

export interface BreadcrumbOutput {
  /** Fires when any clickable item is activated. Also receives the item's index. */
  onItemClick?: (item: BreadcrumbItem, index: number) => void;
}

// ─── Combined props ───────────────────────────────────────────────────────────

export type BreadcrumbProps = BreadcrumbInput & BreadcrumbOutput;

// ─── Implementation ───────────────────────────────────────────────────────────

export function Breadcrumb({
  items,
  separator = "›",
  testId,
  onItemClick,
}: BreadcrumbProps) {
  ensureBreadcrumbCSS();

  return (
    <nav data-testid={testId} aria-label="breadcrumb">
      <Inline gap="xs" align="center" inline>
        {items.map((item, i) => {
          const isLast = i === items.length - 1;
          const isClickable = !isLast && (!!item.href || !!item.onClick);

          const handleClick = () => {
            if (!isClickable) return;
            item.onClick?.();
            onItemClick?.(item, i);
          };

          return (
            <Inline key={i} as="span" inline gap="xs" align="center">
              {/* Separator */}
              {i > 0 && (
                <TextValue
                  as="span"
                  size="sm"
                  tone="faint"
                  nowrap
                >
                  {separator}
                </TextValue>
              )}

              {/* Item */}
              {isClickable ? (
                item.href ? (
                  <a
                    data-testid={`breadcrumb-item-${i}`}
                    href={item.href}
                    className="ds-breadcrumb-link"
                    onClick={(e) => {
                      if (item.onClick) {
                        e.preventDefault();
                      }
                      handleClick();
                    }}
                  >
                    {item.label}
                  </a>
                ) : (
                  <button
                    data-testid={`breadcrumb-item-${i}`}
                    type="button"
                    className="ds-breadcrumb-link"
                    onClick={handleClick}
                  >
                    {item.label}
                  </button>
                )
              ) : (
                <TextValue
                  as="span"
                  size="sm"
                  weight="medium"
                  tone="primary"
                  testId={`breadcrumb-item-${i}`}
                >
                  {item.label}
                </TextValue>
              )}
            </Inline>
          );
        })}
      </Inline>
    </nav>
  );
}
